import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getCompanyMultipleConfig } from '../../../../../../../../../../../lib/api/company';
import { getExecutivesAvailableForFactoring } from '../../../../../../../../../../../lib/api/factoring';
import { rejectFactoringQuote } from '../../../../../../../../../../../lib/api/factoring/updateStatus';
import CenteredCircularProgress from '../../../../../../../../../../../lib/designSystem/CircularProgress';
import {
  validateCompanyTypes,
  validatePermissions,
} from '../../../../../../../../../../../lib/functions/utils';
import { AuthState } from '../../../../../../../../../../../services/redux/authSlice';
import { decrementPendingRequests } from '../../../../../../../../../../../services/redux/notificactionSlice';
import ShoppingCartFactoringCancelled from './components/0_ShoppingCartCancelled';
import ShoppingCartFactoringCreated from './components/1_ShoppingCartCreated';
import ShoppingCartFactoringQuoted from './components/2_ShoppingCartQuoted';
import ShoppingCartFactoringAccepted from './components/3_ShoppingCartAccepted';
import ShoppingCartFactoringInTransfer from './components/4_ShoppingCartInTransfer';
import ShoppingCartFactoringActive from './components/5_ShoppingCartActive';
import ShoppingCartFactoringCompleted from './components/6_ShoppingCartCompleted';
export interface FactoringRequest {
  id: number;
  status: string;
  sender: string;
  recipient: string;
  validUntil: Date;
  paymentDate: Date;
  amount: number;
  extraData: boolean;
  relation?: any;
  businessId: number;
}

const componentsMap: any = {
  Creada: ShoppingCartFactoringCreated,
  Revisada: ShoppingCartFactoringCreated,
  'En revisión': ShoppingCartFactoringCreated,
  Borrador: ShoppingCartFactoringCreated,
  Cotizada: ShoppingCartFactoringQuoted,
  Rechazada: ShoppingCartFactoringCancelled,
  Aceptada: ShoppingCartFactoringAccepted,
  Activa: ShoppingCartFactoringActive,
  Completada: ShoppingCartFactoringCompleted,
  'En giro': ShoppingCartFactoringInTransfer,
};

const Summary = (props: any) => {
  const {
    shoppingCartType,
    openShoppingCart,
    setOpenShoppingCart,
    selectedRequest,
    setSelectedRequest,
    setSuccessOpen,
    setFailureOpen,
    setUp,
    executiveCreating,
    businessId,
    subProduct,
    setSubProduct,
    onlySimulation,
    openDatesSelection,
    setOpenDatesSelection,
    setBackwards,
    formData,
    setFormData,
    resetRequestToOriginal,
    selectedId,
    availableArrearTables,
    documentsToApply,
    setDocumentsToApply,
  } = props;
  const [loading, setLoading] = useState(true);
  const [requestConfiguration, setRequestConfiguration] = useState({});
  const [executivesPortfolio, setExecutivesPortfolio] = useState([]);
  const [executivesAll, setExecutivesAll] = useState([]);
  const dispatch = useDispatch();

  const handleRejectQuote = async () => {
    setLoading(true);
    try {
      await rejectFactoringQuote(dispatch, {
        id: selectedRequest.quoteActive.id,
        status: 'REJECTED',
      });
      dispatch(decrementPendingRequests());
      await setUp();
      setSuccessOpen(`Se ha rechazado la solicitud ${selectedRequest.id}.`);
    } catch (error) {
      setFailureOpen('Lamentablemente ha ocurrido un error');
    }
    setLoading(false);
    setOpenShoppingCart(false);
  };

  const fetchRequestConfiguration = async () => {
    try {
      const canChangeArrearRate = validatePermissions(
        user,
        'factoring_request_edit_arrear_rate',
      );
      const multipleConfig = await getCompanyMultipleConfig(dispatch, [
        'system_accounting_expensesHasIva',
        'fixed-arrear-rate-factoringRequest',
        'factoring_operations_system_flag',
        'system_operations_enable_draft_status',
        'system_operations_block_business_in_blacklist',
        'system_operations_block_stakeholder_in_blacklist',
        'system_factoring_default_exchange_days',
        'system_factoring_can_create_request_expenses',
      ]);
      const expensesHasIVA =
        multipleConfig['system_accounting_expensesHasIva'] === '1';
      const fixedArrearRate = multipleConfig[
        'fixed-arrear-rate-factoringRequest'
      ]
        ? parseFloat(multipleConfig['fixed-arrear-rate-factoringRequest'])
        : undefined;
      const factoringOperationsSystemFlag =
        multipleConfig['factoring_operations_system_flag'];
      const draftEnabled =
        multipleConfig['system_operations_enable_draft_status'] === '1';
      const blockBusinessInBlacklist =
        multipleConfig['system_operations_block_business_in_blacklist'] === '1';
      const blockStakeholderInBlacklist =
        multipleConfig['system_operations_block_stakeholder_in_blacklist'] ===
        '1';
      const factoringDefaultExchangeDays = multipleConfig[
        'system_factoring_default_exchange_days'
      ]
        ? Number(multipleConfig['system_factoring_default_exchange_days'])
        : 0;
      const canCreateRequestExpenses =
        multipleConfig['system_factoring_can_create_request_expenses'] === '1';
      setRequestConfiguration({
        ...requestConfiguration,
        expensesHasIVA,
        fixedArrearRate: fixedArrearRate
          ? canChangeArrearRate
            ? false
            : true
          : false,
        factoringOperationsSystemFlag,
        draftEnabled,
        blockBusinessInBlacklist,
        blockStakeholderInBlacklist,
        factoringDefaultExchangeDays,
        canCreateRequestExpenses,
      });
      setFormData({
        ...formData,
        arrearRate: fixedArrearRate ? fixedArrearRate : 0,
        expenses: [
          {
            name: 'Otros gastos',
            amount: 0,
            hasIva: expensesHasIVA,
          },
        ],
        exchangeDays: factoringDefaultExchangeDays,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchExecutives = async () => {
    const response = await getExecutivesAvailableForFactoring(
      dispatch,
      selectedRequest.businessIdentifier,
    );
    setExecutivesPortfolio(response.usersWithBusinessInPortfolio);
    setExecutivesAll(response.allUsers);
  };

  useEffect(() => {
    (async () => {
      await fetchRequestConfiguration();
      await fetchExecutives();
      setLoading(false);
    })().catch(console.error);
  }, []);

  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const financingHub = validateCompanyTypes(user, 'FinancingHub');

  if (loading) return <CenteredCircularProgress type="dialog" />;

  const Component = componentsMap[shoppingCartType];

  return (
    <div>
      <Component
        subProduct={subProduct}
        setSubProduct={setSubProduct}
        openShoppingCart={openShoppingCart}
        setOpenShoppingCart={setOpenShoppingCart}
        selectedRequest={selectedRequest}
        setSelectedRequest={setSelectedRequest}
        setSuccessOpen={setSuccessOpen}
        setFailureOpen={setFailureOpen}
        inReview={
          shoppingCartType === 'En revisión' ||
          selectedRequest.status === 'Borrador'
        }
        setUp={setUp}
        setLoading={setLoading}
        executiveCreating={executiveCreating}
        businessId={businessId}
        onlySimulation={
          shoppingCartType === 'Creada' ? !financingHub : onlySimulation
        }
        openDatesSelection={openDatesSelection}
        setOpenDatesSelection={setOpenDatesSelection}
        setBackwards={setBackwards}
        formData={formData}
        setFormData={setFormData}
        handleRejectQuote={
          shoppingCartType === 'Cotizada' ? handleRejectQuote : undefined
        }
        requestConfiguration={requestConfiguration}
        resetRequestToOriginal={resetRequestToOriginal}
        selectedId={selectedId}
        availableArrearTables={availableArrearTables}
        executivesPortfolio={executivesPortfolio}
        executivesAll={executivesAll}
        documentsToApply={documentsToApply}
        setDocumentsToApply={setDocumentsToApply}
      />
    </div>
  );
};

export default Summary;
